import React, { useEffect, useRef, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import SettingsHeader from 'pages/authenticated/layout/settingsLayout/settingsHeader/SettingsHeader';
import Input from 'component/atom/forms/input/input/Input';
import styles from './NewForm.module.scss';
import { formsEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import { usePost } from 'hooks/usePost';
import { isEmpty } from 'lodash';
import Modal from 'component/hoc/modal/Modal';
import FormPreview from 'component/organism/workflow/forms/form/formPreview/FormPreview';
import FormFields from 'component/organism/form-responses/newForm/FormFields';
import toast from 'react-hot-toast';
import { formValidationSchema } from './factory/FormValidationSchema';
import AddFormButton from './AddFormButton';

const NewForm = () => {
  const containerRef = useRef(null);

  const navigate = useNavigate();
  const { id, formId } = useParams();

  const [showPreview, setShowPreview] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  const { data: formCurrent, refetch } = useFetch(
    ['formCurrent', id, formId],
    formsEndpoints.getForm,
    {
      enable: false,
    },
    {
      formId: formId,
    }
  );

  const { mutate: createForm, isPending: isAdding } = usePost({
    onSuccessToast: 'Form Added Successfully',
    toastType: 'add',
  });

  const { mutate: updateForm, isPending: isUpdating } = usePost({
    onSuccessToast: 'Form Update Successfully',
    toastType: 'add',
  });

  useEffect(() => {
    if (formId) {
      refetch();
    }
  }, [formId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...(formId && { id: formId }),
      botId: id || '',
      formName: formCurrent?.data?.formName || '',
      formTitle: formCurrent?.data?.formTitle || '',
      buttonText: formCurrent?.data?.buttonText || '',
      formDetails: (formCurrent?.data?.formDetails || []).map((fd) => {
        const normalized = { ...fd };
        if (fd.fieldType === 1) {
          normalized.textTag = typeof fd.textTag === 'number' ? fd.textTag : 0;
        } else {
          delete normalized.textTag;
        }
        return normalized;
      }),
    },
    validationSchema: formValidationSchema,

    onSubmit: (values) => {
      const payload = {
        ...values,
        formDetails: values.formDetails.map((fd) => {
          if (fd.fieldType === 1) {
            return {
              ...fd,
              textTag: typeof fd.textTag === 'number' ? fd.textTag : 0,
            };
          }
          const { textTag, ...rest } = fd;
          return rest;
        }),
      };
      setIsSubmitAttempted(false);
      isEmpty(formId)
        ? createForm(
            {
              url: formsEndpoints.createForm,
              data: payload,
            },
            {
              onSuccess: () => {
                navigate(-1);
              },
            }
          )
        : updateForm(
            {
              url: formsEndpoints.createForm,
              data: payload,
            },
            {
              onSuccess: () => {
                navigate(-1);
              },
            }
          );
    },
  });

  const addNewField = (fieldType, fields) => {
    const base = { ...fields, fieldType };
    const withTag = fieldType === 1 ? { ...base, textTag: 0 } : { ...base };

    formik.setFieldValue('formDetails', [
      ...formik.values.formDetails,
      withTag,
    ]);
    setActiveDropdown((prev) => !prev);
  };

  const handleClick = () => {
    setActiveDropdown((prev) => !prev);
  };
  const handleSubmit = () => {
    setIsSubmitAttempted(true);
    formik.handleSubmit();
  };

  useEffect(() => {
    if (isSubmitAttempted && Object.keys(formik.errors).length > 0) {
      toast.error('Please fill all required fields');
    }
    setTimeout(() => {
      setIsSubmitAttempted(false);
    }, 500);
  }, [formik.errors, isSubmitAttempted]);

  return (
    <form
      className={styles['new-form-container']}
      onSubmit={formik.handleSubmit}
      ref={containerRef}
    >
      <SettingsHeader
        title={formId ? 'Edit Form' : 'Create New Form'}
        rightButtons={[
          {
            label: 'Cancel',
            opicBackground: true,
            func: () => navigate(-1),
            className: styles['cancel-btn'],
          },
          ...(formId
            ? [
                {
                  label: 'Preview',
                  opicBackground: true,
                  func: () => setShowPreview(true),
                },
              ]
            : []),
          {
            label: 'Save',
            type: 'save',
            isLoading: isAdding || isUpdating,
            func: handleSubmit,
          },
        ]}
      />
      <div className={styles['card']}>
        <Input
          label='Form Name'
          placeholder='Enter Name'
          name='formName'
          value={formik.values.formName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.formName && formik.errors.formName}
          touched={formik.touched.formName}
        />
        <Input
          label='Form Title'
          placeholder='Enter Title'
          name='formTitle'
          value={formik.values.formTitle}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.formTitle && formik.errors.formTitle}
          touched={formik.touched.formTitle}
        />
      </div>
      <div className={`${styles['card']}`}>
        <Input
          label='Button'
          placeholder='Enter Name'
          name='buttonText'
          value={formik.values.buttonText}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          inputClassname={styles.button}
          error={formik.touched.buttonText && formik.errors.buttonText}
          touched={formik.touched.buttonText && formik.errors.buttonText}
        />
      </div>
      {typeof formik.errors.formDetails === 'string' && (
        <span className={styles.error}>{formik.errors.formDetails}</span>
      )}

      <FormikProvider value={formik}>
        <FormFields />
      </FormikProvider>

      <AddFormButton
        activeDropdown={activeDropdown}
        setActiveDropdown={setActiveDropdown}
        containerRef={containerRef}
        handleClick={handleClick}
        addNewField={addNewField}
      />

      <Modal
        isOpen={showPreview}
        title={formCurrent?.data?.formName}
        onClose={() => setShowPreview(false)}
      >
        <FormPreview formId={formId} />
      </Modal>
    </form>
  );
};

export default NewForm;
